import { useState, useRef, useEffect } from 'react';
import { FolderKanban, ChevronDown, Check } from 'lucide-react';
import { DismissBackdrop } from '../DismissBackdrop';

/**
 * A dedicated "Projects" checklist dropdown to quickly filter the task views to one or
 * more projects. Shown on both the List and Board views, alongside the other filters.
 * Reads/writes the scope subset of the page's existing filter state.
 */
export function ProjectFilterDropdown({ scopes, selectedIds, onChange }: {
  scopes: { id: string; name: string; color: string }[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = new Set(selectedIds);
  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  }

  return (
    <>
    {/* #121: a tap outside only closes the menu - it does not also open the
        card underneath. The dropdown is raised above the backdrop. */}
    {open && <DismissBackdrop onDismiss={() => setOpen(false)} />}
    <div className={`relative${open ? ' z-30' : ''}`} ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 px-2.5 py-1 bg-bg-input border border-border rounded text-sm text-text-primary hover:border-border-hover">
        <FolderKanban size={14} className="text-text-muted" />
        {selected.size > 0 ? `Projects (${selected.size})` : 'Projects'}
        <ChevronDown size={12} className="text-text-muted" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1.5 min-w-[220px] max-h-72 overflow-y-auto bg-bg-surface border border-border-hover rounded-lg p-1.5 shadow-xl">
          {scopes.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-text-muted">No projects</div>
          ) : scopes.map(s => {
            const on = selected.has(s.id);
            return (
              <button key={s.id} type="button" onClick={() => toggle(s.id)}
                className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-left text-sm ${on ? 'bg-accent/10 text-text-primary' : 'text-text-secondary hover:bg-bg-hover'}`}>
                <span className="w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0"
                  style={{ borderColor: on ? s.color : '#636b88', background: on ? s.color : 'transparent' }}>
                  {on && <Check size={10} className="text-white" />}
                </span>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                <span className="flex-1 truncate">{s.name}</span>
              </button>
            );
          })}
          {selected.size > 0 && (
            <button type="button" onClick={() => onChange([])}
              className="mt-1 w-full text-left px-2 py-1.5 text-xs text-text-muted hover:text-text-primary border-t border-border">
              Clear projects
            </button>
          )}
        </div>
      )}
    </div>
    </>
  );
}
